/**
 * Shared aircraft / TLB autofill helpers for MRO workbench forms (EDR, Action Taken Form, etc.).
 */

export const CYCLE_LABELS = [
  { key: 'ng', label: 'Ng' },
  { key: 'nf', label: 'Nf' },
  { key: 'np', label: 'Np' },
  { key: 'ct', label: 'CT' },
  { key: 'pt', label: 'PT' },
  { key: 'imp', label: 'IMP' },
  { key: 'n1', label: 'N1' },
  { key: 'n2', label: 'N2' },
  { key: 'creep', label: 'Creep' }
];

export const emptyCycles = () =>
  CYCLE_LABELS.reduce((acc, { key }) => {
    acc[key] = '';
    return acc;
  }, {});

export function getAircraftIdFromWorkOrder(workOrder) {
  const ac = workOrder?.aircraftId;
  if (!ac) return null;
  if (typeof ac === 'object' && ac._id) return String(ac._id);
  return String(ac);
}

function sortTsnByDateDesc(tsnArray) {
  if (!Array.isArray(tsnArray) || !tsnArray.length) return [];
  return [...tsnArray].sort((a, b) => {
    const ta = new Date(a.asOnDate || a.manufacturingDate || 0).getTime();
    const tb = new Date(b.asOnDate || b.manufacturingDate || 0).getTime();
    return tb - ta;
  });
}

export function getAircraftHoursFromAc(ac) {
  if (!ac) return '';
  const fh = ac.flightHours;
  if (fh != null && fh !== '' && Number.isFinite(Number(fh)) && Number(fh) > 0) {
    return String(fh);
  }
  for (const entry of sortTsnByDateDesc(ac.tsn)) {
    const p = String(entry?.period || '').trim();
    const pv = String(entry?.periodValue || '').trim();
    if ((p === 'Hours' || p.toLowerCase() === 'hours') && pv) return pv;
    const af = entry?.additionalFields;
    if (af && typeof af === 'object') {
      const h = af.Hours ?? af.hours ?? af.HOURS;
      if (h != null && String(h).trim() !== '') return String(h).trim();
    }
  }
  return '';
}

export function getAircraftLandingsFromAc(ac) {
  if (!ac) return '';
  for (const entry of sortTsnByDateDesc(ac.tsn)) {
    if (entry?.landings != null && String(entry.landings).trim() !== '') return String(entry.landings);
  }
  return '';
}

export function formatAircraftTypeLine(ac) {
  if (!ac || typeof ac !== 'object') return '';
  const bits = [];
  if (ac.aircraftType) bits.push(String(ac.aircraftType).trim());
  const mm = [ac.manufacturer, ac.model].filter((x) => x && String(x).trim()).join(' ').trim();
  if (mm) bits.push(mm);
  return bits.join(' — ').trim();
}

/** Align with TLB / live usage: LH → engine #1, RH → engine #2 (see technicalLogBook.js). */
function preferredEngineSlotFromPosition(position) {
  const text = String(position || '').trim().toUpperCase();
  if (!text) return null;
  if (text.includes('LH') || text.includes('LEFT') || text === '1' || text.endsWith('1')) return 1;
  if (text.includes('RH') || text.includes('RIGHT') || text === '2' || text.endsWith('2')) return 2;
  return null;
}

function enginePositionSortKey(position) {
  const slot = preferredEngineSlotFromPosition(position);
  if (slot === 1) return 0;
  if (slot === 2) return 1;
  return 2;
}

function pickEngineAssemblies(assemblies) {
  const list = Array.isArray(assemblies) ? assemblies : [];
  const engines = list.filter((a) => {
    const tn = String(a?.typeName || '').toLowerCase();
    return tn.includes('engine');
  });
  engines.sort((a, b) => {
    const ka = enginePositionSortKey(a.position);
    const kb = enginePositionSortKey(b.position);
    if (ka !== kb) return ka - kb;
    return String(a.serialNumber || '').localeCompare(String(b.serialNumber || ''));
  });
  return engines;
}

function engineHoursFromAssembly(asm) {
  const recs = asm?.timeSinceRecords;
  if (!Array.isArray(recs)) return '';
  const tsnHours = recs.find(
    (r) =>
      r.type === 'TSN' &&
      r.elapsedTime?.value &&
      String(r.elapsedTime.unit || '').toLowerCase().includes('hour')
  );
  if (tsnHours?.elapsedTime) {
    return `${tsnHours.elapsedTime.value} ${tsnHours.elapsedTime.unit || ''}`.trim();
  }
  const any = recs.find((r) => r.elapsedTime?.value && r.elapsedTime?.unit);
  return any ? `${any.elapsedTime.value} ${any.elapsedTime.unit}`.trim() : '';
}

function cyclesFromAssembly(asm) {
  const c = emptyCycles();
  const snv = asm?.sinceNewValues;
  if (!Array.isArray(snv)) return c;
  for (const row of snv) {
    const p = String(row?.period || '').trim();
    const val = String(row?.assemblyValue ?? '').trim();
    if (!p || !val) continue;
    const pl = p.toLowerCase();
    const hit = CYCLE_LABELS.find(
      ({ key, label }) => pl === key.toLowerCase() || pl === label.toLowerCase()
    );
    if (hit) c[hit.key] = val;
  }
  return c;
}

function formatTcfScalar(val) {
  if (val == null || val === '') return '';
  if (typeof val === 'number' && Number.isFinite(val)) {
    if (Math.abs(val - Math.round(val)) < 1e-9) return String(Math.round(val));
    return String(val);
  }
  return String(val).trim();
}

function mergeTotalsCustomFieldsIntoCarriedForward(tcf, entry) {
  const mix = entry?.totalsCustomFields;
  if (!mix || typeof mix !== 'object') return tcf && typeof tcf === 'object' ? { ...tcf } : {};
  const out = tcf && typeof tcf === 'object' ? { ...tcf } : {};
  for (const [key, val] of Object.entries(mix)) {
    if (typeof key !== 'string' || !key.startsWith('totalCarriedForward.')) continue;
    const baseKey = key.slice(key.lastIndexOf('.') + 1);
    const cur = out[baseKey];
    const hasPrimary = !(cur == null || cur === '');
    if (hasPrimary) continue;
    if (val == null || val === '') continue;
    out[baseKey] = val;
  }
  return out;
}

export function pickLatestApprovedTlb(entries) {
  const list = Array.isArray(entries) ? entries : [];
  const approved = list
    .filter((e) => e?.workflowStatus === 'APPROVED')
    .sort((a, b) => {
      const at = new Date(a.approvedAt || a.updatedAt || a.createdAt || 0).getTime();
      const bt = new Date(b.approvedAt || b.updatedAt || b.createdAt || 0).getTime();
      return bt - at;
    });
  const latest = approved[0];
  if (!latest) return null;
  const raw =
    latest.totalCarriedForward && typeof latest.totalCarriedForward === 'object' ? latest.totalCarriedForward : {};
  const totalCarriedForward = mergeTotalsCustomFieldsIntoCarriedForward(raw, latest);
  return { entry: latest, totalCarriedForward };
}

export function mergeTlbCarriedForwardIntoForm(base, tcf) {
  if (!tcf || typeof tcf !== 'object') return { ...base };
  const next = { ...base };
  const v = formatTcfScalar;

  if (tcf.airframeHours != null && String(tcf.airframeHours).trim() !== '') {
    next.aircraftHours = v(tcf.airframeHours);
  }
  const ldg = tcf.landings ?? tcf.totalLandings;
  if (ldg != null && String(ldg).trim() !== '') {
    next.aircraftLandings = v(ldg);
  }
  if (tcf.engine1Hours != null && String(tcf.engine1Hours).trim() !== '') {
    next.engine1Hours = v(tcf.engine1Hours);
  }
  if (tcf.engine2Hours != null && String(tcf.engine2Hours).trim() !== '') {
    next.engine2Hours = v(tcf.engine2Hours);
  }

  if (tcf.lhEngineCyclesCT != null && String(tcf.lhEngineCyclesCT).trim() !== '') {
    next.lhEngineCyclesCT = v(tcf.lhEngineCyclesCT);
  }
  if (tcf.lhEngineCyclesPT != null && String(tcf.lhEngineCyclesPT).trim() !== '') {
    next.lhEngineCyclesPT = v(tcf.lhEngineCyclesPT);
  }
  if (tcf.lhEngineCyclesIMP != null && String(tcf.lhEngineCyclesIMP).trim() !== '') {
    next.lhEngineCyclesIMP = v(tcf.lhEngineCyclesIMP);
  }
  if (tcf.rhEngineCyclesCT != null && String(tcf.rhEngineCyclesCT).trim() !== '') {
    next.rhEngineCyclesCT = v(tcf.rhEngineCyclesCT);
  }
  if (tcf.rhEngineCyclesPT != null && String(tcf.rhEngineCyclesPT).trim() !== '') {
    next.rhEngineCyclesPT = v(tcf.rhEngineCyclesPT);
  }
  if (tcf.rhEngineCyclesIMP != null && String(tcf.rhEngineCyclesIMP).trim() !== '') {
    next.rhEngineCyclesIMP = v(tcf.rhEngineCyclesIMP);
  }

  return next;
}

function mergeCycleGap(targetCycles, assemblyCycles, key) {
  const cur = String(targetCycles[key] || '').trim();
  if (cur) return targetCycles[key];
  const fromAsm = String(assemblyCycles[key] || '').trim();
  return fromAsm || '';
}

function assignEngineAssemblyToSlot(next, slot, asm) {
  if (!asm || (slot !== 1 && slot !== 2)) return next;
  const serialKey = slot === 1 ? 'engine1Serial' : 'engine2Serial';
  const hoursKey = slot === 1 ? 'engine1Hours' : 'engine2Hours';
  if (asm.serialNumber) next[serialKey] = asm.serialNumber;
  const hours = engineHoursFromAssembly(asm);
  if (hours && !String(next[hoursKey] || '').trim()) next[hoursKey] = hours;
  return next;
}

export function mergeEngineAssemblyGapFill(base, assemblies) {
  const engines = pickEngineAssemblies(assemblies);
  let next = { ...base };

  for (const asm of engines) {
    const slot = preferredEngineSlotFromPosition(asm.position);
    if (slot === 1 || slot === 2) {
      next = assignEngineAssemblyToSlot(next, slot, asm);
    }
  }

  let fallbackSlot = 0;
  for (const asm of engines) {
    if (preferredEngineSlotFromPosition(asm.position)) continue;
    fallbackSlot += 1;
    if (fallbackSlot > 2) break;
    next = assignEngineAssemblyToSlot(next, fallbackSlot, asm);
  }

  return next;
}

export function formatApprovedDateLabel(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}
